import {createStyles} from '@mantine/core';
import {useSelector} from '@xstate/react';
import {produce} from 'immer';
import React, {RefObject, useCallback, useEffect, useRef, useState} from 'react';
import isEqual from 'react-fast-compare';

import {trackEvent} from 'analytics/Analytics';
import {FixedPlateModalType} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import AISuggestedLayoutBadge from 'samples/plate-maps/annotate-plate-modal/ai-assistant/AISuggestedLayoutBadge';
import useAIRoleAnnotationSuggestion from 'samples/plate-maps/annotate-plate-modal/ai-assistant/useAIRoleAnnotationSuggestion';
import AnnotatePlateSettings from 'samples/plate-maps/annotate-plate-modal/AnnotatePlateSettings';
import ConcentrationLayerSettings from 'samples/plate-maps/annotate-plate-modal/ConcentrationLayerSettings';
import MetadataLayerSettings from 'samples/plate-maps/annotate-plate-modal/MetadataLayerSettings';
import ParameterLayerSettings from 'samples/plate-maps/annotate-plate-modal/ParameterLayerSettings';
import WellRoleAssignmentSettings from 'samples/plate-maps/annotate-plate-modal/WellRoleAssignmentSettings';
import WellVolumeSettings from 'samples/plate-maps/annotate-plate-modal/WellVolumeSettings';
import {ConcentrationLayerMachineProvider} from 'samples/plate-maps/concentration-layers/ConcentrationLayerMachineProvider';
import {
  ConcentrationSettings,
  DilutionSettings as DilutionSettingsType,
} from 'samples/plate-maps/concentration-layers/machine/ConcentrationLayerMachine';
import {
  ConcentrationLayersProps,
  getConcentrationAttribute,
  MetadataLayersProps,
  PickableLayersProps,
} from 'samples/plate-maps/display-layer-controls/PlateMapDisplayLayerSidebar';
import {
  confirmDeleteSchemaField,
  toggleSchemaField,
} from 'samples/plate-maps/display-layer-controls/WellSchemaFieldControlUtils';
import usePlateMapDisplayAttributeState from 'samples/plate-maps/hooks/usePlateMapDisplayAttributeState';
import PlateMapPreviewSection from 'samples/plate-maps/plate-map-editor/PlateMapPreviewSection';
import {
  AssignmentGroup,
  AssignmentGroupValue,
  CONCENTRATION_ATTRIBUTE_SENTINEL,
  DisplayLayerMetadata,
  DisplayLayerOptionSection,
  METADATA_ATTRIBUTE_SENTINEL,
  PARAMETER_ATTRIBUTE_SENTINEL,
  PlateDataParameter,
  PlateMap,
  PlateMapDisplayLayerAttribute,
  PlateMapWellDisplayLayer,
  SchematizedAttribute,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
} from 'samples/plate-maps/PlateMapConstants';
import {
  calculateConcentrationPreviewPlateMap,
  clearPlateMapLayer,
  getDisplayLayerAttributeKey,
  getTextLayerAssignmentGroups,
  getParameterAttribute,
  getParameterLayerDisplayOptions,
} from 'samples/plate-maps/PlateMapUtil';
import {PlateMapSidebar} from 'samples/plate-maps/sidebar/PlateMapSidebar';
import getTestProps from 'test/lib/cypress/getTestProps';
import {useDebounce} from 'ui-library/hooks/useDebounce';
import {CenteredSpinner} from 'ui-library/Spinner';

import {useScratchPlateConcentrationLayerActor} from './ScratchPlateConcentrationLayerProvider';
import {useScratchPlateMetadataLayerActor} from './ScratchPlateMetadataLayerProvider';
import {confirmDeleteParameterLayer, toggleParameterLayer} from './ScratchPlateParameterLayerControls';
import {useScratchPlateParameterLayerActor} from './ScratchPlateParameterLayerProvider';

const SCRATCH_PLATE_MAP_UPDATE_DEBOUNCE_MS = 50;

// Stable fallback so looking up a layer with no matching parameter doesn't hand out a fresh array
// reference on every render (breaks referential-equality checks like effect dependency arrays).
const EMPTY_PLANNED_VALUES: PlateDataParameter['plannedValues'] = [];

function getPlannedValuesForLayer(
  plateDataParameters: Array<PlateDataParameter>,
  layerId: string
): PlateDataParameter['plannedValues'] {
  return (
    plateDataParameters.find((parameter) => parameter.versionId === layerId)?.plannedValues ??
    EMPTY_PLANNED_VALUES
  );
}

/**
 * Merge the concentration layer machine's current name for `layerId` into `plateMap.concentrationLayerInfoById`.
 * `calculateConcentrationPreviewPlateMap` only updates well concentration values, not layer metadata, so
 * without this merge a layer rename made in the machine would be overwritten when the preview is committed.
 */
function syncLayerName(plateMap: PlateMap, layerId: string, machineName: string): PlateMap {
  if (plateMap.concentrationLayerInfoById == null) {
    return plateMap;
  }
  const existing = plateMap.concentrationLayerInfoById[layerId];
  if (existing == null || existing.name === machineName) {
    return plateMap;
  }
  return {
    ...plateMap,
    concentrationLayerInfoById: {
      ...plateMap.concentrationLayerInfoById,
      [layerId]: {...existing, name: machineName},
    },
  };
}

/**
 * Concentration settings panel without an Apply button.
 * Auto-commits concentration values to the plate map via a debounced effect.
 * When the active layer changes, applies immediately.
 */
function AutoApplyConcentrationLayerSettings({
  plateMap,
  setPlateMap,
  selectedWellIndices,
  deselectWells,
}: {
  plateMap: PlateMap;
  setPlateMap: (m: PlateMap) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
}): JSX.Element | null {
  const actor = useScratchPlateConcentrationLayerActor();
  const {layers, activeLayerId} = useSelector(actor, (state) => state.context);
  const activeLayer = activeLayerId ? (layers.get(activeLayerId) ?? null) : null;
  const plateMapRef = useRef(plateMap);
  plateMapRef.current = plateMap;

  // Debounced apply on settings or selection changes
  const debouncedActiveLayer = useDebounce(activeLayer, SCRATCH_PLATE_MAP_UPDATE_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedActiveLayer != null) {
      const concentrationPreview = calculateConcentrationPreviewPlateMap(
        debouncedActiveLayer,
        plateMapRef.current,
        selectedWellIndices
      );
      if (concentrationPreview == null) {
        return;
      }
      setPlateMap(
        syncLayerName(concentrationPreview, debouncedActiveLayer.layerId, debouncedActiveLayer.name)
      );
    }
    // Leave plateMap and setPlateMap out of the dependency array, since we only want to perform updates to
    // the overall plate state when the concentration values (debouncedActiveLayer) or focused wells
    // (selectedWellIndices) change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedActiveLayer, selectedWellIndices]);

  if (activeLayer == null) {
    return null;
  }

  const existingConcentrationLayerNames: Set<string> = plateMap.concentrationLayerInfoById
    ? new Set(
        Object.entries(plateMap.concentrationLayerInfoById)
          .filter(([id]) => id !== activeLayer.layerId)
          .map(([, info]) => info.name)
      )
    : new Set();

  return (
    <ConcentrationLayerSettings
      activeLayer={activeLayer}
      existingConcentrationLayerNames={existingConcentrationLayerNames}
      onUpdateLayerName={(layerId, name) => {
        actor.send({type: 'updateLayerName', layerId, name});
      }}
      onUpdateConcentrationSettings={(layerId, settings: Partial<ConcentrationSettings>) => {
        actor.send({type: 'updateConcentrationSettings', layerId, settings});
      }}
      onUpdateLayerDilutionSettings={(layerId, settings: Partial<DilutionSettingsType>) => {
        actor.send({type: 'updateLayerDilutionSettings', layerId, settings});
      }}
      // Behavior decision:
      // - Scratch plate design: concentration edits auto-apply via the debounced effect above.
      // - "Submit" in this panel only clears current well selection; it does not trigger an apply.
      concentrationInlineAction={{
        label: 'Submit',
        disabled: selectedWellIndices.size === 0,
        onClick: deselectWells,
      }}
    />
  );
}

/**
 * Metadata settings panel that auto-applies a typed value to the selected wells. Mirrors
 * {@link AutoApplyConcentrationLayerSettings}: the in-progress "brush" value lives on the active
 * layer in the metadata machine (`draftValue`) so it can't leak across layers, and a debounced
 * effect on the active layer applies it. Name edits are propagated to `plateMap.metadataLayerInfoById`.
 */
function AutoApplyMetadataLayerSettings({
  plateMap,
  setPlateMap,
  selectedWellIndices,
  onUpdateLayerType,
}: {
  plateMap: PlateMap;
  setPlateMap: (m: PlateMap) => void;
  selectedWellIndices: Set<number>;
  onUpdateLayerType: (layerId: string, layerType: LeafType) => void;
}): JSX.Element | null {
  const actor = useScratchPlateMetadataLayerActor();
  const {layers, activeLayerId} = useSelector(actor, (state) => state.context);
  const activeLayer = activeLayerId ? (layers.get(activeLayerId) ?? null) : null;
  const plateMapRef = useRef(plateMap);
  plateMapRef.current = plateMap;

  // Debounced apply on draft-value or selection changes. `draftValue === undefined` means the user
  // hasn't entered a value yet, so we don't touch the wells; `null` (cleared input) is applied.
  const debouncedActiveLayer = useDebounce(activeLayer, SCRATCH_PLATE_MAP_UPDATE_DEBOUNCE_MS);
  useEffect(() => {
    if (
      debouncedActiveLayer == null ||
      debouncedActiveLayer.draftValue === undefined ||
      selectedWellIndices.size === 0
    ) {
      return;
    }
    const {layerId, draftValue} = debouncedActiveLayer;
    setPlateMap(
      produce(plateMapRef.current, (draft) => {
        for (let i = 0; i < draft.wells.length; i++) {
          if (!selectedWellIndices.has(i)) {
            continue;
          }
          draft.wells[i].metadataLayers ??= {};
          draft.wells[i].metadataLayers![layerId] = draftValue;
        }
      })
    );
    // Leave plateMap/setPlateMap out of the deps; include selectedWellIndices so selecting more wells
    // re-applies the current draft value (matching concentration behaviour).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedActiveLayer, selectedWellIndices]);

  if (activeLayer == null) {
    return null;
  }

  const existingLayerNames: Set<string> = new Set(
    Object.entries(plateMap.metadataLayerInfoById ?? {})
      .filter(([id]) => id !== activeLayer.layerId)
      .map(([, info]) => info.name.trim())
  );

  return (
    <MetadataLayerSettings
      key={activeLayer.layerId}
      activeLayer={activeLayer}
      existingLayerNames={existingLayerNames}
      pendingValue={activeLayer.draftValue ?? null}
      hasSelectedWells={selectedWellIndices.size > 0}
      onUpdateLayerName={(layerId, name) => {
        actor.send({type: 'updateLayerName', layerId, name});
        setPlateMap(
          produce(plateMap, (draft) => {
            if (draft.metadataLayerInfoById?.[layerId]) {
              draft.metadataLayerInfoById[layerId].name = name;
            }
          })
        );
      }}
      onUpdateLayerType={onUpdateLayerType}
      onUpdatePendingValue={(value) => {
        actor.send({type: 'setDraftValue', layerId: activeLayer.layerId, value});
      }}
    />
  );
}

/**
 * Parameter settings panel: select wells, then click a label to assign its value to them. The
 * parameter's name and type are fixed (derived from the referenced procedure parameter); labels are
 * seeded from the parameter's planned values (locked) plus whatever's already on the plate. Mirrors
 * {@link TextFieldAssignmentSettings}/{@link NumberFieldAssignmentSettings}'s assignment-group
 * pattern rather than {@link AutoApplyMetadataLayerSettings}'s auto-apply brush.
 */
function ParameterAssignmentSettings({
  plateMap,
  setPlateMap,
  selectedWellIndices,
  deselectWells,
  plateDataParameters,
  assignmentGroupsById,
  setAssignmentGroupsById,
}: {
  plateMap: PlateMap;
  setPlateMap: (m: PlateMap) => void;
  selectedWellIndices: Set<number>;
  deselectWells: () => void;
  plateDataParameters: Array<PlateDataParameter>;
  /**
   * Same map (and setter) AnnotatePlateSettings passes to TextFieldAssignmentSettings for well
   * schema fields — sharing it here keeps a TEXT parameter's sidebar labels and plate-grid well
   * colors in sync, rather than each computing its own independent color assignment.
   */
  assignmentGroupsById: {[id: string]: AssignmentGroup<AssignmentGroupValue>};
  setAssignmentGroupsById: (groups: {[id: string]: AssignmentGroup<AssignmentGroupValue>}) => void;
}): JSX.Element | null {
  const actor = useScratchPlateParameterLayerActor();
  const {layers, activeLayerId} = useSelector(actor, (state) => state.context);
  const activeLayer = activeLayerId ? (layers.get(activeLayerId) ?? null) : null;

  if (activeLayer == null) {
    return null;
  }

  const plannedValues = getPlannedValuesForLayer(plateDataParameters, activeLayer.layerId);

  return (
    <ParameterLayerSettings
      key={activeLayer.layerId}
      plateMap={plateMap}
      onEditPlateMap={setPlateMap}
      activeLayer={activeLayer}
      plannedValues={plannedValues}
      selectedWellIndices={selectedWellIndices}
      deselectWells={deselectWells}
      assignmentGroupsById={assignmentGroupsById}
      setAssignmentGroupsById={setAssignmentGroupsById}
    />
  );
}

export interface DefineScratchPlateMapStepProps {
  plateMap: PlateMap;
  setPlateMap: (plateMap: PlateMap) => void;
  containerSchemaId: string | null;
  wellSchemaLayers: Array<DisplayLayerMetadata> | null;
  selectedWellSchemaFieldIds: Set<string>;
  /** Procedure data parameters available to this plate (empty outside a procedure context). */
  plateDataParameters: Array<PlateDataParameter>;
  onAddSelectedSchemaField: (schemaFieldId: string) => void;
  onRemoveSelectedSchemaField: (schemaFieldId: string) => void;
  onAddConcentrationLayer: () => void;
  onRemoveConcentrationLayer: (layerId: string) => void;
  onAddMetadataLayer: () => void;
  onRemoveMetadataLayer: (layerId: string) => void;
  onUpdateMetadataLayerType: (layerId: string, layerType: LeafType) => void;
  onAddParameterLayer: (parameter: PlateDataParameter) => void;
  onRemoveParameterLayer: (layerId: string) => void;
  modalRef?: RefObject<HTMLDivElement>;
  maxPlateViewHeight: string;
  /** Identifies the hosting modal in analytics events emitted by this step. */
  analyticsModalType: FixedPlateModalType;
}

/**
 * Step 2 of ScratchPlateDesignModal.
 *
 * Closely mirrors DefinePlateMapStep / AnnotatePlateStep but:
 * - Hides CONTENTS, PARAMETERS, and TEST_METADATA sections
 * - Concentration changes auto-save to plate map (debounced) without an Apply button
 * - Does not use PlateModalMachine; receives plate map state via props
 */
export default function DefineScratchPlateMapStep({
  plateMap,
  setPlateMap,
  wellSchemaLayers,
  selectedWellSchemaFieldIds,
  plateDataParameters,
  onAddSelectedSchemaField,
  onRemoveSelectedSchemaField,
  onAddConcentrationLayer,
  onRemoveConcentrationLayer,
  onAddMetadataLayer,
  onRemoveMetadataLayer,
  onUpdateMetadataLayerType,
  onAddParameterLayer,
  onRemoveParameterLayer,
  modalRef,
  maxPlateViewHeight,
  analyticsModalType,
}: DefineScratchPlateMapStepProps): JSX.Element {
  const {classes} = useStyles();
  const actor = useScratchPlateConcentrationLayerActor();
  const concentrationMachineCtx = useSelector(actor, (state) => state.context);
  const metadataActor = useScratchPlateMetadataLayerActor();
  const metadataMachineCtx = useSelector(metadataActor, (state) => state.context);
  const parameterActor = useScratchPlateParameterLayerActor();
  const parameterMachineCtx = useSelector(parameterActor, (state) => state.context);

  const displayState = usePlateMapDisplayAttributeState(plateMap, WELL_ROLE_ATTRIBUTE_SENTINEL, false);
  const {
    assignmentGroupsPerAttribute,
    setAssignmentGroupsPerAttribute,
    selectedAttribute,
    displayLayer,
    setSelectedAttribute,
  } = displayState;

  const [selectedWellIndices, setSelectedWellIndices] = useState<Set<number>>(new Set());
  const [plateMapWithPreview, setPlateMapWithPreview] = useState<PlateMap | null>(null);
  const [previewWellRoles, setPreviewWellRoles] = useState<boolean>(false);

  const aiAnalyticsMetadata = {modalType: analyticsModalType, plateId: plateMap.id};
  const {
    aiSuggestedPlateMap,
    clearAISuggestion,
    isAIPreview,
    handleSubmitAIPrompt,
    applyAISuggestion,
    discardAISuggestion,
  } = useAIRoleAnnotationSuggestion({
    plateMap,
    setPlateMapWithPreview,
    onApply: (suggestedPlateMap) => {
      trackEvent(STORABLE_ANALYTICS_ACTIONS.APPLIED_ROLE_ANNOTATION_AI_SUGGESTION, aiAnalyticsMetadata);
      setPlateMap(suggestedPlateMap);
    },
    onDiscard: () => {
      trackEvent(STORABLE_ANALYTICS_ACTIONS.DISCARDED_ROLE_ANNOTATION_AI_SUGGESTION, aiAnalyticsMetadata);
    },
  });

  const previewSectionRef = useRef<HTMLDivElement>(null);
  const previewHeaderRef = useRef<HTMLDivElement>(null);
  const previewHeaderTextRef = useRef<HTMLParagraphElement>(null);

  function deselectWells(): void {
    setSelectedWellIndices(new Set());
  }

  function handleSetActiveLayer(layerId: string | null): void {
    deselectWells();
    if (actor.getSnapshot().context.activeLayerId !== layerId) {
      actor.send({type: 'setActiveLayer', layerId});
    }
  }

  function handleSetActiveMetadataLayer(layerId: string | null): void {
    deselectWells();
    if (metadataActor.getSnapshot().context.activeLayerId !== layerId) {
      metadataActor.send({type: 'setActiveLayer', layerId});
    }
  }

  function handleSetActiveParameterLayer(layerId: string | null): void {
    deselectWells();
    if (parameterActor.getSnapshot().context.activeLayerId !== layerId) {
      parameterActor.send({type: 'setActiveLayer', layerId});
    }
  }

  const onSelectAttribute = useCallback(
    (attr: PlateMapDisplayLayerAttribute): void => {
      if (typeof attr === 'object' && attr.type === CONCENTRATION_ATTRIBUTE_SENTINEL) {
        handleSetActiveLayer(attr.key);
        handleSetActiveMetadataLayer(null);
        handleSetActiveParameterLayer(null);
      } else if (typeof attr === 'object' && attr.type === METADATA_ATTRIBUTE_SENTINEL) {
        handleSetActiveLayer(null);
        handleSetActiveMetadataLayer(attr.key);
        handleSetActiveParameterLayer(null);
      } else if (typeof attr === 'object' && attr.type === PARAMETER_ATTRIBUTE_SENTINEL) {
        handleSetActiveLayer(null);
        handleSetActiveMetadataLayer(null);
        handleSetActiveParameterLayer(attr.key);
      } else {
        handleSetActiveLayer(null);
        handleSetActiveMetadataLayer(null);
        handleSetActiveParameterLayer(null);
      }
      displayState.setSelectedAttribute(attr);
    },
    // handleSetActive* only close over stable actor refs and setters; list those instead of the
    // functions to avoid a new callback identity every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [actor, metadataActor, parameterActor, displayState.setSelectedAttribute]
  );

  // Sync the display attribute to the active metadata layer. On an id change, run the full select
  // path (which deselects wells); for name/type edits on the same layer, only refresh the attribute
  // so the current well selection is preserved.
  const previousActiveMetadataLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    const activeId = metadataMachineCtx.activeLayerId;
    const activeLayer = activeId != null ? metadataMachineCtx.layers.get(activeId) : null;
    if (activeLayer == null) {
      previousActiveMetadataLayerIdRef.current = null;
      return;
    }
    const attr = {
      key: activeLayer.layerId,
      type: METADATA_ATTRIBUTE_SENTINEL,
      name: activeLayer.name,
      dataType: activeLayer.type,
    } as const;
    const activeLayerIdChanged = previousActiveMetadataLayerIdRef.current !== activeId;
    previousActiveMetadataLayerIdRef.current = activeId;
    if (activeLayerIdChanged) {
      onSelectAttribute(attr);
    } else {
      setSelectedAttribute(attr);
    }
  }, [metadataMachineCtx.activeLayerId, metadataMachineCtx.layers, onSelectAttribute, setSelectedAttribute]);

  // Sync the display attribute to the active parameter layer, mirroring the metadata-layer effect.
  const previousActiveParameterLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    const activeId = parameterMachineCtx.activeLayerId;
    const activeLayer = activeId != null ? parameterMachineCtx.layers.get(activeId) : null;
    if (activeLayer == null) {
      previousActiveParameterLayerIdRef.current = null;
      return;
    }
    const plannedValues = getPlannedValuesForLayer(plateDataParameters, activeLayer.layerId);
    const attr = getParameterAttribute(activeLayer, plannedValues);
    const activeLayerIdChanged = previousActiveParameterLayerIdRef.current !== activeId;
    previousActiveParameterLayerIdRef.current = activeId;
    if (activeLayerIdChanged) {
      onSelectAttribute(attr);
    } else {
      setSelectedAttribute(attr);
    }
  }, [
    parameterMachineCtx.activeLayerId,
    parameterMachineCtx.layers,
    plateDataParameters,
    onSelectAttribute,
    setSelectedAttribute,
  ]);

  // Sync the display attribute to the active concentration layer, mirroring the metadata-layer effect.
  const previousActiveConcentrationLayerIdRef = useRef<string | null>(null);
  useEffect(() => {
    const activeId = concentrationMachineCtx.activeLayerId;
    const activeLayer = activeId != null ? concentrationMachineCtx.layers.get(activeId) : null;
    if (activeLayer == null) {
      previousActiveConcentrationLayerIdRef.current = null;
      return;
    }
    const attr = getConcentrationAttribute(activeLayer);
    const activeLayerIdChanged = previousActiveConcentrationLayerIdRef.current !== activeId;
    previousActiveConcentrationLayerIdRef.current = activeId;
    if (activeLayerIdChanged) {
      onSelectAttribute(attr);
    } else {
      setSelectedAttribute(attr);
    }
  }, [
    concentrationMachineCtx.activeLayerId,
    concentrationMachineCtx.layers,
    onSelectAttribute,
    setSelectedAttribute,
  ]);

  // When the plate map changes for an active text metadata layer, drop the cached assignment groups
  // so newly-entered values get colored while preserving existing value->color assignments. Metadata
  // layers need this because they write values straight to well.metadataLayers via a debounced
  // auto-apply brush and never call setAssignmentGroupsById themselves — this scan is the only way
  // their colors get discovered. Parameter attributes are deliberately excluded: ParameterLayerSettings
  // already calls setAssignmentGroupsById directly on add/edit/delete (mirroring
  // TextFieldAssignmentSettings), and re-scanning on top of that would drop any manually-added label
  // that isn't yet on a well or in plannedValues, since this scan only preserves entries it can
  // rediscover from the plate map / planned values.
  useEffect(() => {
    if (
      displayLayer !== PlateMapWellDisplayLayer.METADATA_TEXT ||
      typeof selectedAttribute !== 'object' ||
      selectedAttribute.type !== METADATA_ATTRIBUTE_SENTINEL
    ) {
      return;
    }

    const key = getDisplayLayerAttributeKey(selectedAttribute);
    const currentGroups = assignmentGroupsPerAttribute[key] ?? {};
    const mergedGroups = getTextLayerAssignmentGroups(plateMap, selectedAttribute, currentGroups);
    if (isEqual(mergedGroups, currentGroups)) {
      return;
    }

    setAssignmentGroupsPerAttribute({...assignmentGroupsPerAttribute, [key]: mergedGroups});
  }, [
    assignmentGroupsPerAttribute,
    displayLayer,
    plateMap,
    selectedAttribute,
    setAssignmentGroupsPerAttribute,
  ]);

  const concentrationLayersProps: ConcentrationLayersProps = {
    enableAddRemoveLayers: true,
    concentrationLayers: Array.from(concentrationMachineCtx.layers.values()),
    activeLayerId: concentrationMachineCtx.activeLayerId,
    onAddLayer: onAddConcentrationLayer,
    onRemoveLayer: onRemoveConcentrationLayer,
    onSelectLayer: (layerId) => {
      handleSetActiveLayer(layerId);
      const layer = layerId != null ? concentrationMachineCtx.layers.get(layerId) : null;
      if (layer != null) {
        const preview = calculateConcentrationPreviewPlateMap(layer, plateMap, selectedWellIndices);
        if (preview != null) {
          setPlateMap(syncLayerName(preview, layer.layerId, layer.name));
        }
      }
    },
  };

  const metadataLayersProps: MetadataLayersProps = {
    enableAddRemoveLayers: true,
    metadataLayers: Array.from(metadataMachineCtx.layers.values()),
    activeLayerId: metadataMachineCtx.activeLayerId,
    onAddLayer: onAddMetadataLayer,
    onRemoveLayer: onRemoveMetadataLayer,
    onSelectLayer: (layerId) => {
      handleSetActiveMetadataLayer(layerId);
    },
  };

  // Parameters picker: available options come from the procedure context (empty outside a
  // procedure); selected ids are the parameter layers currently on the plate. Add/remove use the
  // scratch-plate parameter helpers (which detect discarded values via `well.parameterLayers`), then
  // drive the scratch-plate parameter machine + plate map.
  const selectedParameterLayerIds = new Set(parameterMachineCtx.layers.keys());
  function onToggleParameterLayer({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }): void {
    toggleParameterLayer({
      newSelectedIds,
      oldSelectedIds: selectedParameterLayerIds,
      plateMap,
      onConfirmAdd: (versionId) => {
        const parameter = plateDataParameters.find((p) => p.versionId === versionId);
        if (parameter != null) {
          // Adding sets the layer active; the sync effect selects it. Selecting the raw
          // DATA_PARAMETER attribute here would deselect it (the paint flow doesn't recognize it).
          onAddParameterLayer(parameter);
        }
      },
      onConfirmDelete: onRemoveParameterLayer,
      onLaunchConfirmDeleteModal,
    });
  }
  function onRemoveParameterLayerFromPicker(versionId: string): void {
    confirmDeleteParameterLayer({
      plateMap,
      layerId: versionId,
      onConfirmDelete: onRemoveParameterLayer,
    });
  }
  const parameterLayersPickerProps: PickableLayersProps = {
    enableAddRemoveLayers: plateDataParameters.length > 0,
    // Surface added layers as sentinel attributes so selecting one opens the value editor; the add
    // dropdown lists the procedure's parameters from context separately.
    availableLayers: getParameterLayerDisplayOptions(Array.from(parameterMachineCtx.layers.values())),
    selectedLayerIds: selectedParameterLayerIds,
    onToggleLayer: onToggleParameterLayer,
    onRemoveLayer: onRemoveParameterLayerFromPicker,
  };

  // build wellSchemaLayersProps
  const wellSchemaLayersForPicker = wellSchemaLayers ?? [];
  function onToggleSchemaFieldLayer({
    newSelectedIds,
    onLaunchConfirmDeleteModal,
    toggledAttribute,
  }: {
    newSelectedIds: Array<string>;
    onLaunchConfirmDeleteModal: () => void;
    toggledAttribute: SchematizedAttribute;
  }): void {
    toggleSchemaField({
      newSelectedIds,
      oldSelectedIds: selectedWellSchemaFieldIds,
      plateMap,
      initialPlateMap: plateMap,
      onConfirmAdd: (schemaFieldId: string) => {
        onAddSelectedSchemaField(schemaFieldId);
        onSelectAttribute(toggledAttribute);
      },
      onConfirmDelete: onRemoveSelectedSchemaField,
      onLaunchConfirmDeleteModal,
    });
  }
  function onRemoveSchemaFieldLayer(schemaFieldId: string): void {
    confirmDeleteSchemaField({
      plateMap,
      initialPlateMap: plateMap,
      schemaFieldId,
      onConfirmDelete: onRemoveSelectedSchemaField,
    });
  }
  const wellSchemaLayersPickerProps: PickableLayersProps = {
    enableAddRemoveLayers: wellSchemaLayersForPicker.length > 0,
    availableLayers: wellSchemaLayersForPicker,
    selectedLayerIds: selectedWellSchemaFieldIds,
    onToggleLayer: onToggleSchemaFieldLayer,
    onRemoveLayer: onRemoveSchemaFieldLayer,
  };

  // for AnnotatePlateSettings
  function setAssignmentGroupsForSelectedAttribute(newGroupsById: {
    [id: string]: AssignmentGroup<AssignmentGroupValue>;
  }): void {
    setAssignmentGroupsPerAttribute({
      ...assignmentGroupsPerAttribute,
      [getDisplayLayerAttributeKey(selectedAttribute)]: newGroupsById,
    });
  }
  // Keep a stable reference to plateMap so onUpdatePattern doesn't stale-close over an old value
  const plateMapRef = useRef(plateMap);
  plateMapRef.current = plateMap;
  const onUpdatePattern = useCallback((newPlateMap: PlateMap): void => {
    if (newPlateMap != null && !isEqual(newPlateMap, plateMapRef.current)) {
      setPlateMap(newPlateMap);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // for plateMapDisplayParams and AnnotatePlateSettings
  const assignmentGroupsById =
    assignmentGroupsPerAttribute[getDisplayLayerAttributeKey(selectedAttribute)] ?? {};

  // for PlateMapPreviewSection. An AI suggestion is a complete layout, so every well is previewed; a
  // manual pattern preview only covers the selected wells.
  const previewedWellIndices = isAIPreview
    ? new Set(plateMap.wells.map((_, i) => i))
    : plateMapWithPreview != null
      ? selectedWellIndices
      : new Set<number>();
  const plateMapDisplayParams = {
    plateMap: plateMapWithPreview ?? plateMap,
    displayLayer: previewWellRoles ? PlateMapWellDisplayLayer.WELL_ROLE : displayLayer,
    attribute: previewWellRoles ? WELL_ROLE_ATTRIBUTE_SENTINEL : selectedAttribute,
    assignmentGroupsById,
    groupedValidationErrors: {},
  };

  return (
    <div
      className={classes.stepContents}
      {...getTestProps({component: 'DefineScratchPlateMapStep', element: 'root'})}
    >
      {wellSchemaLayers == null && <CenteredSpinner />}
      {wellSchemaLayers != null && (
        <ConcentrationLayerMachineProvider actor={actor}>
          <PlateMapSidebar
            plateMapType={PlateMapType.DESIGN}
            plateMap={plateMap}
            selectedAttributeKey={getDisplayLayerAttributeKey(selectedAttribute)}
            setSelectedAttribute={onSelectAttribute}
            assignmentGroups={assignmentGroupsPerAttribute}
            disabledSections={[]}
            hiddenSections={[DisplayLayerOptionSection.CONTENTS, DisplayLayerOptionSection.TEST_METADATA]}
            groupedValidationErrors={{}}
            wellSchemaLayersProps={wellSchemaLayersPickerProps}
            concentrationLayersProps={concentrationLayersProps}
            metadataLayersProps={metadataLayersProps}
            parameterLayersProps={parameterLayersPickerProps}
            isDisplayingScratchPlate={true}
          />
          <div className={classes.border} />
          <div className={classes.mainContent}>
            <PlateMapPreviewSection
              plateMapDisplayParams={plateMapDisplayParams}
              displayLayer={displayLayer}
              selectedWellIndices={selectedWellIndices}
              setSelectedWellIndices={setSelectedWellIndices}
              previewedWellIndices={previewedWellIndices}
              totalWells={plateMap.wells.length}
              previewWellRoles={previewWellRoles}
              onTogglePreviewWellRoles={() => {
                setPreviewWellRoles(!previewWellRoles);
              }}
              shouldDisableWellRolePreview={plateMap.wells.every((well) => well.role == null)}
              onClickClear={() => {
                const updatedPlateMap = clearPlateMapLayer(
                  plateMap,
                  displayLayer,
                  selectedAttribute,
                  selectedWellIndices
                );
                setPlateMap(updatedPlateMap);
                deselectWells();
              }}
              previewSectionRef={previewSectionRef}
              previewHeaderRef={previewHeaderRef}
              previewHeaderTextRef={previewHeaderTextRef}
              modalRef={modalRef}
              maxPlateViewHeight={maxPlateViewHeight}
              showFinalMeasurements={false}
              plateMapType={PlateMapType.DESIGN}
              headerLeadingSlot={
                isAIPreview && displayLayer === PlateMapWellDisplayLayer.WELL_ROLE ? (
                  <AISuggestedLayoutBadge />
                ) : undefined
              }
            />
            <div className={classes.settingsBorder} />
            <AnnotatePlateSettings
              plateMap={plateMap}
              onEditPlateMap={(newPlateMap) => {
                setPlateMap(newPlateMap);
              }}
              selectedWellIndices={selectedWellIndices}
              deselectWells={deselectWells}
              displayLayer={displayLayer}
              selectedAttribute={selectedAttribute}
              assignmentGroupsById={assignmentGroupsById}
              setAssignmentGroupsById={setAssignmentGroupsForSelectedAttribute}
              wellRoleSettings={
                <WellRoleAssignmentSettings
                  plateMap={plateMap}
                  onEditPlateMap={(newPlateMap) => {
                    setPlateMap(newPlateMap);
                  }}
                  selectedWellIndices={selectedWellIndices}
                  deselectWells={deselectWells}
                  setPlateMapWithPreview={setPlateMapWithPreview}
                  onSubmitAIPrompt={handleSubmitAIPrompt}
                  onApplyAISuggestion={applyAISuggestion}
                  onDiscardAISuggestion={discardAISuggestion}
                  aiSuggestedPlateMap={aiSuggestedPlateMap}
                  clearAISuggestion={clearAISuggestion}
                  onUpdatePattern={onUpdatePattern}
                  showApplyButton={false}
                />
              }
              wellVolumeSettings={
                <WellVolumeSettings
                  plateMap={plateMap}
                  setPlateMap={setPlateMap}
                  selectedWellIndices={selectedWellIndices}
                  deselectWells={deselectWells}
                />
              }
              concentrationSettings={
                <AutoApplyConcentrationLayerSettings
                  plateMap={plateMap}
                  setPlateMap={setPlateMap}
                  selectedWellIndices={selectedWellIndices}
                  deselectWells={deselectWells}
                />
              }
              metadataSettings={
                <AutoApplyMetadataLayerSettings
                  plateMap={plateMap}
                  setPlateMap={setPlateMap}
                  selectedWellIndices={selectedWellIndices}
                  onUpdateLayerType={onUpdateMetadataLayerType}
                />
              }
              parameterSettings={
                <ParameterAssignmentSettings
                  plateMap={plateMap}
                  setPlateMap={setPlateMap}
                  selectedWellIndices={selectedWellIndices}
                  deselectWells={deselectWells}
                  plateDataParameters={plateDataParameters}
                  assignmentGroupsById={assignmentGroupsById}
                  setAssignmentGroupsById={setAssignmentGroupsForSelectedAttribute}
                />
              }
            />
          </div>
        </ConcentrationLayerMachineProvider>
      )}
    </div>
  );
}

const useStyles = createStyles((theme) => ({
  stepContents: {
    display: 'flex',
    maxHeight: 'calc(100vh - 280px)',
    overflow: 'hidden',
    margin: '-16px',
    padding: '16px',
  },
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    margin: '-16px 16px -16px 0',
  },
  mainContent: {
    display: 'flex',
    flex: 1,
    overflow: 'hidden',
  },
  settingsBorder: {
    flex: '0 0 0px',
    borderRight: `1px solid ${theme.colors.gray[2]}`,
    margin: '-16px 16px',
  },
}));
